import type { AuthTokens } from '@/types/auth';

// Хранилище токенов сессии (framework-agnostic). Access — только в памяти; refresh — в
// localStorage (задокументированное исключение, см. CLAUDE-react.md). React-обвязка — в
// stores/authContext.tsx, транспорт читает access напрямую отсюда.
export const REFRESH_TOKEN_KEY = 'data1861-refresh-token';
// Маркер имперсонации: факт «сессия имперсонирована» + актор (реальный админ). Хранится отдельно, т.к.
// claim `act` есть только в токене первого обмена (после refresh Identix его не переиздаёт), а сессия
// переживает reload через refresh-токен — маркер должен пережить тоже.
export const IMPERSONATION_KEY = 'data1861-impersonation';

/** Актор имперсонации (реальный админ, который вошёл «от имени»). */
export interface Impersonator {
  readonly sub: string;
  readonly username: string;
}

let accessToken: string | undefined;

export const tokenStore = {
  getAccess(): string | undefined {
    return accessToken;
  },

  getRefresh(): string | undefined {
    return localStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined;
  },

  hasSession(): boolean {
    return localStorage.getItem(REFRESH_TOKEN_KEY) !== null;
  },

  setTokens(tokens: AuthTokens): void {
    accessToken = tokens.accessToken;
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  },

  clear(): void {
    accessToken = undefined;
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
  },

  /** Пометить сессию имперсонированной (при первом обмене кода) — сохраняет актора для баннера. */
  setImpersonation(actor: Impersonator): void {
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(actor));
  },

  /** Актор имперсонации, если сессия имперсонирована; null — обычная сессия. */
  getImpersonation(): Impersonator | null {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<Impersonator>;
      return typeof parsed?.sub === 'string' ? { sub: parsed.sub, username: parsed.username ?? '' } : null;
    } catch {
      return null;
    }
  },

  /** Кросс-таб: очистка refresh-токена в другой вкладке → колбэк (разлогин остальных). */
  subscribeCrossTabClear(callback: () => void): () => void {
    function onStorage(event: StorageEvent): void {
      if (event.key === REFRESH_TOKEN_KEY && !event.newValue) {
        callback();
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  },
};
